import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";

const setValue = (el, v) => {
  Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, "value").set.call(el, v);
  el.dispatchEvent(new Event("change", { bubbles: true }));
};

const labelFor = (el) => {
  if (el.getAttribute("aria-label")) return el.getAttribute("aria-label");
  if (el.id) { const l = document.querySelector(`label[for="${el.id}"]`); if (l) return l.textContent; }
  const prev = el.parentElement?.querySelector("label");
  return prev ? prev.textContent : "";
};

// Replaces the phone's native select picker (which uses the phone language) with an Albanian sheet
export function SelectSheet() {
  const [sel, setSel] = useState(null);

  useEffect(() => {
    const open = (e) => {
      const el = e.target.closest && e.target.closest("select");
      if (!el || el.disabled || el.multiple || el.dataset.native === "1") return;
      e.preventDefault();
      el.blur();
      setSel({
        el,
        host: el.closest('[role="dialog"]'),
        title: labelFor(el).trim(),
        value: el.value,
        options: Array.from(el.options).map((o) => ({ value: o.value, label: o.textContent, disabled: o.disabled })),
      });
    };
    let start = null;
    const onStart = (e) => { const t = e.touches[0]; start = { x: t.clientX, y: t.clientY, moved: false }; };
    const onMove = (e) => { if (!start) return; const t = e.touches[0]; if (Math.abs(t.clientX - start.x) > 8 || Math.abs(t.clientY - start.y) > 8) start.moved = true; };
    const onEnd = (e) => {
      const s = start; start = null;
      const el = e.target.closest && e.target.closest("select");
      if (!el) return;
      if (s && s.moved) { e.preventDefault(); return; }
      open(e);
    };
    document.addEventListener("mousedown", open, true);
    document.addEventListener("touchstart", onStart, { capture: true, passive: true });
    document.addEventListener("touchmove", onMove, { capture: true, passive: true });
    document.addEventListener("touchend", onEnd, { capture: true, passive: false });
    return () => {
      document.removeEventListener("mousedown", open, true);
      document.removeEventListener("touchstart", onStart, { capture: true });
      document.removeEventListener("touchmove", onMove, { capture: true });
      document.removeEventListener("touchend", onEnd, { capture: true });
    };
  }, []);

  if (!sel) return null;
  const pick = (v) => { setValue(sel.el, v); setSel(null); };

  const sheet = (
    <div className={`fixed inset-0 z-[1000] flex items-end justify-center bg-black/40 gs-fade ${sel.host ? "rounded-[inherit] overflow-hidden" : ""}`} onClick={() => setSel(null)} data-testid="select-sheet">
      <div className={`w-full max-w-md bg-white rounded-t-3xl shadow-2xl pb-6 flex flex-col ${sel.host ? "max-h-[90%]" : "max-h-[75vh]"}`} onClick={(e) => e.stopPropagation()}>
        <div className="w-10 h-1.5 bg-slate-200 rounded-full mx-auto mt-2.5 mb-2" />
        {sel.title && <div className="px-5 pb-2 text-sm font-bold text-slate-900">{sel.title}</div>}
        <div className="overflow-y-auto overscroll-contain px-2">
          {sel.options.map((o, i) => (
            <button key={`${o.value}-${i}`} type="button" disabled={o.disabled} onClick={() => pick(o.value)}
              data-testid={`select-sheet-option-${i}`}
              className={`w-full flex items-center justify-between text-left px-3 py-3 rounded-xl text-[15px] ${o.value === sel.value ? "bg-emerald-50 text-emerald-700 font-semibold" : "text-slate-700"} ${o.disabled ? "opacity-40" : "active:bg-slate-100"}`}>
              <span>{o.label}</span>
              {o.value === sel.value && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => setSel(null)} data-testid="select-sheet-close" className="mx-4 mt-2 h-11 rounded-xl bg-slate-100 text-slate-700 font-semibold">Mbyll</button>
      </div>
    </div>
  );
  return sel.host ? createPortal(sheet, sel.host) : sheet;
}
